import React, { useEffect, useRef } from 'react'
import { Howl } from 'howler'
import styles from './MusicPlayer.module.css'
import {
  Rewind,
  Play,
  Pause,
  FastForward
} from 'phosphor-react'
import { SOUNDS } from '../../constants/sounds'
import { useMusic } from '../../contexts/MusicContext'

export default function MusicPlayer() {
  const playPauseSoundRef = useRef<Howl | null>(null)
  const {
    isPlaying,
    currentTime,
    track,
    duration,
    togglePlayPause,
    nextTrack,
    prevTrack,
    isReady
  } = useMusic()

  useEffect(() => {
    playPauseSoundRef.current = new Howl({
      src: [SOUNDS.PLAY_PAUSE],
      volume: 0.5
    })
    return () => {
      playPauseSoundRef.current?.unload()
    }
  }, [])

  const handleTogglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isReady) {
      playPauseSoundRef.current?.play()
      togglePlayPause()
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60).toString()
    const sec = Math.floor(seconds % 60).toString().padStart(2, '0')
    return `${min}:${sec}`
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0

  const handleNextTrack = (e: React.MouseEvent) => {
    e.stopPropagation()
    nextTrack()
  }

  const handlePrevTrack = (e: React.MouseEvent) => {
    e.stopPropagation()
    prevTrack()
  }

  return (
    <div className={styles.musicPlayer} onClick={handleClick}>
      <div className={styles.musicInfo}>
        <img src={track.thumbnail} className={styles.musicThumbnail} alt={track.title} />
        <div className={styles.textInfo}>
          <div className={styles.songName}>{track.title}</div>
          <div className={styles.artistName}>{track.artist}</div>
        </div>
      </div>

      <div className={styles.controls}>
        <button onClick={handlePrevTrack} disabled={!isReady}>
          <Rewind size={24} weight="fill" color="white" />
        </button>

        <button onClick={handleTogglePlayPause} disabled={!isReady}>
          {isPlaying
            ? <Pause size={24} weight="fill" color="white" />
            : <Play size={24} weight="fill" color="white" />}
        </button>

        <button onClick={handleNextTrack} disabled={!isReady}>
          <FastForward size={24} weight="fill" color="white" />
        </button>
      </div>

      <div className={styles.timeBar}>
        <span>{formatTime(currentTime)}</span>
        <div className={styles.progressContainer}>
          <div
            className={styles.progressBar}
            style={{ width: `${progressPercent}%` }}
          ></div>
          <div
            className={styles.progressThumb}
            style={{ left: `${progressPercent}%` }}
          ></div>
        </div>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  )
}